import React, { useMemo } from 'react';
import ErrorBoundary from '@helpcenter/error-boundary/src/error-boundary';
import { isUiModificationsSupportEnabled } from '@helpcenter/feature-flags';
import { getActivationId, getCloudId } from '@helpcenter/meta';
import { UI_MODIFICATIONS_JSM_REQUEST_CREATE_RESOURCE } from '@helpcenter/portal-home-resource';
import { di } from 'react-magnetic-di';
import { getAnalyticsWebClientPromise } from '@atlassian/help-center-common-util/analytics/client';
import { useRelayResource } from '@atlassian/help-center-common-util/resource-router';
import { AnalyticsListener } from '@atlassian/ui-modifications-analytics';
import { UiModificationsRoot } from '@atlassian/ui-modifications-core';
import { DEFAULT } from '../../common/utils/configuration/constants';
import { ForgeAppRenderer } from './forge-app-renderer';
import type { UiModificationsEntryPointProps } from './types';

// TODO: add memo to the below component
const EntryPoint = ({ portalId, requestTypeId, triggerPointKey }: UiModificationsEntryPointProps) => {
    di(UiModificationsRoot, useRelayResource, getActivationId, getCloudId, ForgeAppRenderer);

    const executionContextKey = portalId && requestTypeId ? `${portalId}-${requestTypeId}` : null;
    const cloudId = getCloudId();
    const activationId = getActivationId();

    const { queryReference } = useRelayResource(UI_MODIFICATIONS_JSM_REQUEST_CREATE_RESOURCE);

    const executionContext = useMemo(() => {
        if (!portalId || !requestTypeId) {
            return null;
        }

        return {
            portal: { id: portalId },
            request: { typeId: requestTypeId },
        };
    }, [portalId, requestTypeId]);

    const triggerPointKeyVal = triggerPointKey ?? DEFAULT;

    return (
        <UiModificationsRoot
            executionContext={executionContext}
            queryReference={queryReference}
            cloudId={cloudId}
            activationId={activationId}
            executionContextKey={executionContextKey}
            triggerPointKey={triggerPointKeyVal}
            ForgeAppRenderer={ForgeAppRenderer}
        />
    );
};

export const UiModificationsEntryPoint = (props: UiModificationsEntryPointProps) => {
    di(AnalyticsListener, isUiModificationsSupportEnabled, ErrorBoundary, getAnalyticsWebClientPromise);

    return isUiModificationsSupportEnabled() ? (
        <ErrorBoundary id="uiModifications" packageName="jsmUiModificationsViewRequestCreate">
            <AnalyticsListener
                analyticsWebClient={getAnalyticsWebClientPromise().then((client) => client.getInstance())}
            >
                <EntryPoint {...props} />
            </AnalyticsListener>
        </ErrorBoundary>
    ) : null;
};
